// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import * as ActiveStorage from "@rails/activestorage"
import "./controllers"
import "./pwa_install_prompt"
import "./turbo_extensions"

import PhotoUploader from "./lib/photo_uploader";

initializeImageReloading();
ActiveStorage.start()

// Initialize PhotoUploader globally for Turbo compatibility
if (!window.photo_uploader) {
  window.photo_uploader = new PhotoUploader();
  console.log('PhotoUploader initialized globally');
}

Turbo.StreamActions.scroll_to = function () {
  const target = this.targetElements[0];
  target.scrollIntoView({behavior: "smooth"});
};

// Centralized geolocation handling
window.currentPosition = null;
window.positionWatchId = null;
window.positionUpdateCallbacks = [];

// Initialize geolocation after Turbo load
document.addEventListener("turbo:load", function() {
  console.debug("Turbo loaded, initializing geolocation");
  initializeGeolocation();
});

function initializeImageReloading() {
  // Reload broken images with exponential backoff, up to 5 retries
  document.addEventListener("error", function(e) {
    console.debug("Image error detected, attempting to reload with exponential backoff");

    const img = e.target;
    if (img.tagName !== "IMG") return;

    // Track retry count on the image element
    img._reloadAttempts = (img._reloadAttempts || 0) + 1;
    if (img._reloadAttempts > 5) {
      console.debug("Max retries reached for image, giving up");
      return;
    }

    // Calculate delay: 500ms, 1000ms, 2000ms, 4000ms, 8000ms
    const delay = 500 * Math.pow(2, img._reloadAttempts - 1);

    setTimeout(() => {
      const url = new URL(img.src);
      url.searchParams.set('t', Date.now());
      img.src = url.toString();
    }, delay);
  }, true);
}

// Function to detect if device is mobile
function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
}

function initializeGeolocation() {
  // First, try to get an immediate position
  if (navigator.geolocation) {
    const options = {
      enableHighAccuracy: isMobileDevice(),
      timeout: 30000,
      maximumAge: 0
    };

    // Get initial position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.debug("Initial position received");
        window.currentPosition = position;
        // Notify any callbacks that position was updated
        notifyPositionUpdated(position);
      },
      (error) => {
        console.error("Error getting initial position:", error);
        handleGeolocationError(error);
      },
      options
    );

    // Set up watch for continuous updates
    if (!window.positionWatchId) {
      window.positionWatchId = navigator.geolocation.watchPosition(
        (position) => {
          console.debug("Position updated from watch");
          window.currentPosition = position;
          // Notify any callbacks that position was updated
          notifyPositionUpdated(position);
        },
        (error) => {
          console.error("Error watching position:", error);
          handleGeolocationError(error);
        },
        { enableHighAccuracy: isMobileDevice(), timeout: 30000, maximumAge: 5000 }
      );
    }
  } else {
    console.error("Geolocation not supported in this browser");
    handleGeolocationError({ code: 0, message: "Geolocation not supported in this browser" });
  }
}

// Function to handle geolocation errors with user-friendly messages
function handleGeolocationError(error) {
  let errorMessage = "";

  switch(error.code) {
    case 1: // PERMISSION_DENIED
      errorMessage = "This app is all about locations - it's for explorers! Please allow access to your device's location for the best experience.";
      break;
    case 2: // POSITION_UNAVAILABLE
      errorMessage = "Unable to determine your location. Please check your device's location settings.";
      break;
    case 3: // TIMEOUT
      errorMessage = "Location request timed out. This sometimes happens when GPS signal is weak. Try moving to an area with better signal or check your device's location settings.";
      break;
      
      // Wu: 2025-10-16 Commenting out timeout message for now as it seems to be very common in desktop browsers. https://github.com/kudelabs/seeniq/issues/179
      // return; // Don't show any message on timeout for now
    default:
      errorMessage = "Geolocation is not supported by this browser or device.";
  }

  showLocationError(errorMessage);
}

// Function to display location error message
function showLocationError(message) {
  // Check if we're on a page that needs location (discoveries, map, etc.)
  const mainContent = document.querySelector('main');
  if (!mainContent) return;

  // Only show location error on pages that actually need location
  const needsLocation = window.location.pathname == '/discoveries/index_feed' || window.location.pathname == '/discoveries/index_map' || window.location.pathname == '/';

  if (!needsLocation) {
    console.debug("Page doesn't need location, skipping location error display");
    return;
  }

  // Remove any existing floating error icon and overlay
  const existingIcon = document.querySelector('.location-error-float-icon');
  const existingOverlay = document.querySelector('.location-error-overlay');

  if (existingIcon) {
    return;
  }

  // Create floating error icon
  const floatingIcon = document.createElement('div');
  floatingIcon.className = 'location-error-float-icon';
  floatingIcon.innerHTML = `
    <i class="fas fa-exclamation-triangle"></i>
  `;

  // Store the error message in the icon element for later use
  floatingIcon.dataset.errorMessage = message;

  // Add click handler to toggle error overlay
  floatingIcon.addEventListener('click', toggleLocationErrorOverlay);

  // Add icon to document body (so it floats over everything)
  document.body.appendChild(floatingIcon);
}

// Function to toggle the location error overlay
function toggleLocationErrorOverlay() {
  const existingOverlay = document.querySelector('.location-error-overlay');

  if (existingOverlay) {
    // Hide the overlay
    existingOverlay.remove();
  } else {
    // Show the overlay
    const icon = document.querySelector('.location-error-float-icon');
    if (!icon) return;

    const message = icon.dataset.errorMessage;
    const mainContent = document.querySelector('main');
    if (!mainContent) return;

    // Create error overlay
    const errorOverlay = document.createElement('div');
    errorOverlay.className = 'location-error-overlay';
    errorOverlay.innerHTML = `
      <div class="loading-container">
        <div class="location-error">
          <div class="location-error-header">
            <i class="fas fa-exclamation-triangle fa-3x"></i>
          </div>
          <p>${message}</p>
          <p>
            You can also <a href="/discoveries/bulk_upload_form">upload photos from your photo library</a>.
            <br />
            And you can of course use the <a href="/discoveries/index_map">Explore</a> screen to see discoveries on a map and the <a href="/discoveries/index_feed">Feed</a> screen.
          </p>
          <div class="location-error-buttons">
            <button class="button is-primary" onclick="retryGeolocation()">
              <i class="fas fa-redo"></i>&nbsp;Try Again
            </button>
            <button class="button location-error-close" onclick="toggleLocationErrorOverlay()">
              <i class="fas fa-times"></i>&nbsp;Close
            </button>
          </div>
        </div>
      </div>
    `;

    // Add overlay to main content
    mainContent.appendChild(errorOverlay);
  }
}

window.handleGeolocationError = handleGeolocationError;
window.showLocationError = showLocationError;

// Make toggleLocationErrorOverlay globally accessible
window.toggleLocationErrorOverlay = toggleLocationErrorOverlay;

// Function to retry geolocation
window.retryGeolocation = function() {
  const errorOverlay = document.querySelector('.location-error-overlay');
  const errorIcon = document.querySelector('.location-error-float-icon');

  if (errorOverlay) {
    errorOverlay.remove();
  }
  if (errorIcon) {
    errorIcon.remove();
  }

  initializeGeolocation();
};

// Function to notify all registered callbacks about position updates
function notifyPositionUpdated(position) {
  // Clear any existing location errors when position is successfully obtained
  const errorOverlay = document.querySelector('.location-error-overlay');
  const errorIcon = document.querySelector('.location-error-float-icon');

  if (errorOverlay) {
    errorOverlay.remove();
  }
  if (errorIcon) {
    errorIcon.remove();
  }

  window.positionUpdateCallbacks.forEach(callback => {
    try {
      callback(position);
    } catch (e) {
      console.error("Error in position update callback:", e);
    }
  });
}

// Function to get current position or request it if not available
window.getCurrentPosition = function(highAccuracy = false) {
  return new Promise((resolve, reject) => {
    // If we already have a position, use it immediately
    if (window.currentPosition) {
      console.debug("Using cached position");
      resolve(window.currentPosition);
      return;
    }

    // Otherwise request a new position
    if (navigator.geolocation) {
      const options = {
        enableHighAccuracy: highAccuracy && isMobileDevice(),
        timeout: 30000,
        maximumAge: 30000
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          window.currentPosition = position;
          resolve(position);
        },
        (error) => {
          console.error("Error getting position:", error);
          handleGeolocationError(error);
          reject(error);
        },
        options
      );
    } else {
      const error = { code: 0, message: "Geolocation not supported" };
      console.error("Geolocation not supported");
      handleGeolocationError(error);
      reject(error);
    }
  });
};

// Register for position updates
window.onPositionUpdate = function(callback) {
  if (typeof callback === 'function' && !window.positionUpdateCallbacks.includes(callback)) {
    window.positionUpdateCallbacks.push(callback);
  }
  return callback; // Return the callback so it can be stored and removed later
};

// Unregister from position updates
window.offPositionUpdate = function(callback) {
  const index = window.positionUpdateCallbacks.indexOf(callback);
  if (index !== -1) {
    window.positionUpdateCallbacks.splice(index, 1);
  }
};

// Make calculateDistance globally available for our controllers
window.calculateDistance = function(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Radius of the earth in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in meters
  return distance;
}

function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

(function() {
  if (!document.cookie.match(/browser_time_zone=/)) {
    var tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    document.cookie = 'browser_time_zone=' + encodeURIComponent(tz) + '; path=/; max-age=31536000';
  }
})();
